import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileSignature, X, Edit2, Send, IndianRupee, Sparkles, FileDown, Eye } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import Attachments from "@/components/common/Attachments";
import { openPdfInViewer } from "@/pages/PdfViewer";

export default function InvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get(`/invoices/${id}`);
        setInv(data); setLoading(false);
    };
    useEffect(() => { load(); }, [id]);

    const generateEInvoice = async () => {
        setBusy(true);
        try {
            await api.post(`/e-invoices/${id}/generate`);
            toast.success("IRN generated");
            await load();
        } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
    };

    const voidInvoice = async () => {
        if (!confirm("Void this invoice?")) return;
        await api.post(`/invoices/${id}/void`);
        toast.success("Voided");
        await load();
    };

    const downloadPdf = async () => {
        try {
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${inv.number}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("PDF download failed"); }
    };

    const viewPdf = async () => {
        try {
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            openPdfInViewer(blob, inv.number, navigate);
        } catch (e) { toast.error("PDF preview failed"); }
    };

    if (loading || !inv) return <div className="p-8 text-muted-foreground">Loading…</div>;

    return (
        <>
            <PageHeader
                kicker={inv.type === "estimate" ? "ESTIMATE" : "INVOICE"}
                breadcrumbs={<button className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => navigate("/sales/invoices")}><ArrowLeft className="w-3.5 h-3.5" />Back to invoices</button>}
                title={inv.number}
                subtitle={`${inv.customer_name} • ${fmtDate(inv.date)} • ${inv.currency}`}
                actions={
                    <>
                        <StatusBadge status={inv.status} />
                        {inv.e_invoice?.status === "generated" ? (
                            <span className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-positive/30 bg-positive-soft text-positive">
                                <FileSignature className="w-3.5 h-3.5" /> IRN active
                            </span>
                        ) : inv.currency === "INR" && inv.status !== "void" && inv.status !== "draft" ? (
                            <Button variant="outline" size="sm" onClick={generateEInvoice} disabled={busy} data-testid="invoice-generate-irn">
                                <FileSignature className="w-4 h-4 mr-1.5" /> Generate IRN
                            </Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={viewPdf} data-testid="invoice-view-pdf"><Eye className="w-4 h-4 mr-1.5" />View PDF</Button>
                        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="invoice-download-pdf"><FileDown className="w-4 h-4 mr-1.5" />Download PDF</Button>
                        <Button variant="outline" size="sm" onClick={() => navigate(`/sales/invoices/${id}/edit`)} data-testid="invoice-edit"><Edit2 className="w-4 h-4 mr-1.5" />Edit</Button>
                        {inv.status !== "void" && <Button variant="outline" size="sm" onClick={voidInvoice} data-testid="invoice-void"><X className="w-4 h-4 mr-1.5" />Void</Button>}
                    </>
                }
            />
            <PageBody>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-card border border-border p-6 md:p-8" style={{ borderRadius: 2 }}>
                        <div className="flex items-start justify-between border-b border-border pb-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1 font-mono">TAX INVOICE</div>
                                <div className="font-display text-3xl font-light">{inv.number}</div>
                                <div className="text-[12px] text-muted-foreground mt-1 font-mono">{`$ inv.show --id=${inv.number}`}</div>
                            </div>
                            <div className="text-right text-[12px] font-mono">
                                <div className="text-muted-foreground uppercase tracking-[0.1em] text-[9.5px]">Issue date</div>
                                <div className="tabular-nums">{fmtDate(inv.date)}</div>
                                <div className="text-muted-foreground uppercase tracking-[0.1em] text-[9.5px] mt-2">Due date</div>
                                <div className="tabular-nums">{fmtDate(inv.due_date)}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6 mt-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Bill To</div>
                                <div className="font-medium">{inv.customer_name}</div>
                                <div className="text-[12px] text-muted-foreground mt-0.5">Place of supply: {inv.place_of_supply || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Currency</div>
                                <div>{inv.currency}{inv.exchange_rate !== 1 && ` @ ${inv.exchange_rate}`}</div>
                                {inv.is_inter_state && <div className="text-[11px] text-muted-foreground mt-1">Inter-state supply (IGST)</div>}
                                {inv.is_export && <div className="text-[11px] text-muted-foreground mt-1">Export supply</div>}
                            </div>
                        </div>

                        <table className="fin-table mt-8">
                            <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th><th className="num">Tax</th><th className="num">Total</th></tr></thead>
                            <tbody>
                                {(inv.lines || []).map((l, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div className="font-medium">{l.item_name}</div>
                                            {l.hsn && <div className="text-[11px] text-muted-foreground font-mono">HSN/SAC: {l.hsn}</div>}
                                        </td>
                                        <td className="num">{l.quantity}</td>
                                        <td className="num">{fmtMoney(l.rate, inv.currency)}</td>
                                        <td className="num">{fmtMoney(l.amount, inv.currency)}</td>
                                        <td className="num text-[12px]">{l.tax_rate}% • {fmtMoney(l.tax_amount, inv.currency)}</td>
                                        <td className="num font-medium">{fmtMoney(l.total, inv.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="mt-6 flex justify-end">
                            <div className="w-full md:w-[360px] space-y-1.5 text-[13px]">
                                <Row label="Subtotal" value={fmtMoney(inv.sub_total, inv.currency)} />
                                {inv.discount_total > 0 && <Row label="Discount" value={`− ${fmtMoney(inv.discount_total, inv.currency)}`} />}
                                {inv.cgst_total > 0 && <Row label="CGST" value={fmtMoney(inv.cgst_total, inv.currency)} />}
                                {inv.sgst_total > 0 && <Row label="SGST" value={fmtMoney(inv.sgst_total, inv.currency)} />}
                                {inv.igst_total > 0 && <Row label="IGST" value={fmtMoney(inv.igst_total, inv.currency)} />}
                                {inv.shipping > 0 && <Row label="Shipping" value={fmtMoney(inv.shipping, inv.currency)} />}
                                {inv.round_off !== 0 && <Row label="Round off" value={fmtMoney(inv.round_off, inv.currency)} />}
                                <div className="border-t border-border my-2" />
                                <Row bold large label="Total due" value={fmtMoney(inv.total, inv.currency)} />
                                <Row label="Paid" value={fmtMoney(inv.paid_amount, inv.currency)} />
                                <Row bold className="text-positive" label="Balance" value={fmtMoney(inv.balance_due, inv.currency)} />
                            </div>
                        </div>

                        {inv.notes && <div className="mt-8 text-[12.5px] text-muted-foreground border-t border-border pt-4">Notes: {inv.notes}</div>}
                    </div>

                    <div className="space-y-4">
                        <div className="bg-card border border-border rounded-md p-5">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">E-INVOICE</div>
                            {inv.e_invoice?.irn ? (
                                <div className="space-y-2 text-[12.5px]">
                                    <div><span className="text-muted-foreground">IRN:</span> <span className="font-mono break-all">{inv.e_invoice.irn}</span></div>
                                    <div><span className="text-muted-foreground">Ack No:</span> <span className="font-mono">{inv.e_invoice.ack_no}</span></div>
                                    <div><span className="text-muted-foreground">Ack Date:</span> {fmtDate(inv.e_invoice.ack_date)}</div>
                                    <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={inv.e_invoice.status} /></div>
                                </div>
                            ) : (
                                <div className="text-[12.5px] text-muted-foreground">No IRN generated yet.</div>
                            )}
                        </div>

                        <div className="bg-card border border-border rounded-md p-5">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">PAYMENTS</div>
                            {(inv.payments || []).length === 0 ? (
                                <div className="text-[12.5px] text-muted-foreground">No payments received yet.</div>
                            ) : (
                                <div className="space-y-2 text-[12.5px]">
                                    {inv.payments.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                                            <div>
                                                <div className="font-mono text-[12px]">{p.number}</div>
                                                <div className="text-[11px] text-muted-foreground">{fmtDate(p.date)} • {p.method}</div>
                                            </div>
                                            <div className="font-mono">{fmtMoney(p.amount, p.currency)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-card border border-border rounded-md p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-gold" />
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">COPILOT</div>
                            </div>
                            <p className="text-[12.5px] text-muted-foreground">Ask AI Copilot to summarize this invoice or recommend collection actions.</p>
                            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => navigate("/copilot")}>Open Copilot</Button>
                        </div>
                        <Attachments entityType="invoice" entityId={id} />
                    </div>
                </div>
            </PageBody>
        </>
    );
}

function Row({ label, value, bold, large, className = "" }) {
    return (
        <div className={`flex items-center justify-between ${bold ? "font-medium" : ""} ${large ? "text-[15px]" : ""} ${className}`}>
            <span className="text-muted-foreground">{label}</span>
            <span className="font-mono tabular-nums">{value}</span>
        </div>
    );
}
